/**
 * PLUGINS (PLUGINS.md §6) — the plugins this platform bundles and whether THIS workspace uses each. A workspace owner /
 * admin installs one after reading what it asks for (its permissions) — that confirmation IS the consent; a newer
 * version asking for more says "needs your consent". Uninstalling stops every use (connections, setups, runs).
 * A plugin that ships an EXPERT offers "the plugin and its expert network" (a working expert, wired to the plugin) or
 * the plugin only; an installed one offers "Create network" again. Uninstalling a plugin networks were created from
 * lists them first. Backend: GET /tools/plugins · PUT /tools/plugins/{id} {action, force?} ·
 * POST /registry/packages/plugin:<id>:<expert>/deploy.
 *
 * Lives on the networks landing page's **Plugins** tab (``variant="page"``, everyone browses; owners / admins act) —
 * next to Experts and Registry, where people look for what to add. ``query`` = the tab bar's search.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Loader2, Puzzle } from 'lucide-react';
import { listPlugins, setPluginInstall } from '@stepflow/ui-shared/services/toolsApi.js';
import { createNetworkFromPlugin } from '@stepflow/ui-shared/services/networkRegistryApi.js';
import { resetPluginConnectionTypes } from '@stepflow/ui-shared/hooks/usePluginConnectionTypes.js';
import { confirmDialog } from '@stepflow/ui-shared/components/DialogHost.jsx';
import PluginInstallDialog from './PluginInstallDialog.jsx';
import { describePermission } from './pluginPermissions.js';

export { describePermission };

const btn = 'px-2.5 py-1 text-xs font-medium rounded-md transition-colors';

/** PURE — does a plugin match the search (name, id, publisher, description, the experts it ships)? */
export function pluginMatches(p, query) {
  const q = String(query || '').trim().toLowerCase();
  if (!q) return true;
  return [p?.displayName, p?.id, p?.publisher, p?.description, ...((p?.experts || []).map((x) => x.title))]
    .some((v) => String(v || '').toLowerCase().includes(q));
}

// ``onChanged({created})`` — the host refreshes what depends on it (the sidebar's network list: a network created
// here must appear — in the Expert Workbench for an expert — without a page reload)
export default function PluginsCard({ workspaceRole, showToast = null, variant = 'card', query = '', onChanged = null }) {
  const [plugins, setPlugins] = useState(null);
  const [busy, setBusy] = useState('');
  const isAdmin = ['OWNER', 'ADMIN'].includes(String(workspaceRole || '').toUpperCase());
  const load = useCallback(() => listPlugins().then((d) => setPlugins(d?.plugins || [])).catch(() => setPlugins([])), []);
  const [dialog, setDialog] = useState(null);             // {plugin, mode: install | create}
  const [created, setCreated] = useState(null);           // {networkId, name} — the network just created
  useEffect(() => { load(); }, [load]);
  const page = variant === 'page';
  if (!plugins) return page ? <p className="px-5 py-6 text-xs text-gray-500" data-testid="plugins-loading">Loading…</p> : null;
  if (!plugins.length && !page) return null;
  const shown = plugins.filter((p) => pluginMatches(p, query));

  const submitInstall = async ({ createNetwork, expertId, name }) => {
    const p = dialog.plugin;
    if (dialog.mode !== 'create') await setPluginInstall(p.id, 'install');
    resetPluginConnectionTypes();
    let made = null;
    if (createNetwork) {
      const out = await createNetworkFromPlugin(p.id, expertId, { name });
      made = { networkId: out?.networkId, name };
    }
    setDialog(null);
    setCreated(made);
    await load();
    try { await onChanged?.({ created: made }); } catch { /* the host's refresh is best-effort */ }
    showToast?.(made ? `${name} is ready — create its rooms next.` : `${p.displayName} is installed.`, 'success');
  };

  const uninstall = async (p) => {
    const ok = await confirmDialog({
      title: `Uninstall ${p.displayName}?`,
      message: 'Its connections stop working in every room of this workspace and its setups are no longer offered. You can install it again.',
      confirmLabel: 'Uninstall', danger: true,
    });
    if (!ok) return;
    setBusy(p.id);
    try {
      try {
        await setPluginInstall(p.id, 'uninstall');
      } catch (e) {
        const deps = e?.data?.dependents;
        if (e?.status !== 409 || !deps?.length) throw e;
        const sure = await confirmDialog({
          title: `${deps.length} network${deps.length === 1 ? '' : 's'} need ${p.displayName}`,
          message: `${deps.map((d) => `• ${d.name}`).join('\n')}\n\nTheir connections and setups stop if you uninstall it.`,
          confirmLabel: 'Uninstall anyway', danger: true,
        });
        if (!sure) return;
        await setPluginInstall(p.id, 'uninstall', { force: true });
      }
      resetPluginConnectionTypes();
      await load();
      try { await onChanged?.({ uninstalled: p.id }); } catch { /* best-effort */ }
      showToast?.(`${p.displayName} is uninstalled.`, 'success');
    } catch (e) {
      showToast?.(`Couldn't uninstall (${e.message}).`, 'error');
    } finally {
      setBusy('');
    }
  };

  return (
    <div className={page ? 'px-5 py-4' : 'mx-auto mt-6 max-w-xl rounded-xl border border-gray-200 bg-white p-6 shadow-sm'}
      data-testid="plugins-card">
      <h3 className="flex items-center gap-1.5 text-sm font-semibold text-gray-800"><Puzzle className="h-4 w-4 text-gray-500" aria-hidden />Plugins</h3>
      <p className="mt-1 text-xs text-gray-500">
        Plugins connect the platform to outside systems (a cloud, a code host, a tool). This workspace uses only the ones
        installed here — each asks for what it needs.
        {page && !isAdmin ? ' Workspace owners and admins install them.' : ''}
      </p>
      {page && !shown.length ? (
        <p className="mt-4 text-xs text-gray-500" data-testid="plugins-empty">
          {plugins.length ? 'No plugin matches your search.' : 'No plugins are available yet.'}
        </p>
      ) : null}
      <ul className="mt-3 divide-y divide-gray-100 text-xs">
        {shown.map((p) => {
          const st = p.install || {};
          return (
            <li key={p.id} className="py-2" data-testid={`plugin-${p.id}`}>
              <div className="flex items-center gap-2">
                <span className="font-medium text-gray-800">{p.displayName}</span>
                <span className="text-gray-400">{p.version} · {p.publisher}</span>
                <span className={`rounded px-1.5 py-px text-[10px] font-medium ${st.installed
                  ? (st.needsConsent ? 'bg-amber-50 text-amber-800' : 'bg-emerald-50 text-emerald-700') : 'bg-gray-100 text-gray-600'}`}
                data-testid={`plugin-state-${p.id}`}>
                  {st.installed ? (st.needsConsent ? 'needs your consent' : 'installed') : 'not installed'}
                </span>
                {isAdmin ? (
                  <span className="ml-auto inline-flex gap-1">
                    {st.installed && !st.needsConsent && (p.experts || []).length ? (
                      <button type="button" disabled={!!busy} onClick={() => setDialog({ plugin: p, mode: 'create' })}
                        data-testid={`plugin-create-network-${p.id}`}
                        className={`${btn} border border-gray-200 text-gray-700 hover:bg-gray-50 disabled:opacity-50`}>Create network</button>
                    ) : null}
                    {!st.installed || st.needsConsent ? (
                      <button type="button" disabled={!!busy} onClick={() => setDialog({ plugin: p, mode: 'install' })} data-testid={`plugin-install-${p.id}`}
                        className={`${btn} inline-flex items-center gap-1 bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50`}>
                        {busy === p.id ? <Loader2 className="h-3 w-3 animate-spin" aria-hidden /> : null}
                        {st.needsConsent ? 'Review & allow' : 'Install'}
                      </button>
                    ) : null}
                    {st.installed ? (
                      <button type="button" disabled={!!busy} onClick={() => uninstall(p)} data-testid={`plugin-uninstall-${p.id}`}
                        className={`${btn} text-rose-700 hover:bg-rose-50 disabled:opacity-50`}>Uninstall</button>
                    ) : null}
                  </span>
                ) : null}
              </div>
              {p.description ? <p className="mt-0.5 text-gray-600">{p.description}</p> : null}
              {(p.experts || []).length ? (
                <p className="mt-0.5 text-[11px] text-gray-500" data-testid={`plugin-experts-${p.id}`}>
                  Ships the expert{p.experts.length === 1 ? '' : 's'}: {p.experts.map((x) => x.title).join(' · ')}
                </p>
              ) : null}
              {(p.mcpServers || []).length ? (
                <p className="mt-0.5 text-[11px] text-gray-500" data-testid={`plugin-mcp-${p.id}`}>
                  Adds to the MCP catalog: {p.mcpServers.map((x) => x.name).join(' · ')}
                </p>
              ) : null}
              {(p.permissions || []).length ? (
                <p className="mt-0.5 text-[11px] text-gray-500" data-testid={`plugin-permissions-${p.id}`}>
                  Asks to: {p.permissions.map(describePermission).join(' · ')}
                </p>
              ) : null}
            </li>
          );
        })}
      </ul>
      {created?.networkId ? (
        <p className="mt-2 text-xs text-emerald-700" data-testid="plugin-network-created">
          {created.name} was created —{' '}
          <a href={`/network/${encodeURIComponent(created.networkId)}`} className="font-medium underline">open it</a> to create its rooms.
        </p>
      ) : null}
      <PluginInstallDialog open={!!dialog} plugin={dialog?.plugin} mode={dialog?.mode} onSubmit={submitInstall}
        onCancel={() => setDialog(null)} />
    </div>
  );
}
